import { TextField, InputAdornment, Tooltip, IconButton, Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { normalizeIban } from '../helpers/iban';
import { useLanguage } from '../i18n/LanguageContext';

interface IbanFieldProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    error?: boolean;
    helperText?: React.ReactNode;
    placeholder: string;
    /** Ustawia wartość numeru konta — używane do usunięcia spacji po wyjściu z pola. */
    setValue: (value: string) => void;
    /**
     * Etykieta nad polem. Formularz osobowy ma opis w lewej kolumnie wiersza,
     * więc go nie powtarzamy; B2B nie ma takiej kolumny i bez niej pole byłoby nieopisane.
     */
    inlineLabels?: boolean;
    name?: string;
    sx?: object;
}

/**
 * Pole numeru konta wspólne dla formularza osobowego i B2B.
 *
 * Nazwa banku nie jest już pytana w formularzu — wyznaczamy ją z numeru IBAN
 * dopiero przy wysyłce (patrz FormPagePerson / FormPageB2B).
 */
const IbanField = ({
    value, onChange, onBlur, error, helperText, setValue, placeholder,
    inlineLabels = false, name = 'accountNumber', sx,
}: IbanFieldProps) => {
    const { t } = useLanguage();
    const hint = t('field.ibanHint');

    const label = (text: string) => (
        <Typography sx={{ fontSize: '14px', textAlign: 'left', marginBottom: '4px' }}>
            {text}
        </Typography>
    );

    return (
        <Box sx={{ width: '100%' }}>
            {inlineLabels && label(t('field.iban'))}
            <TextField
                fullWidth
                sx={sx}
                name={name}
                value={value}
                onChange={onChange}
                // Wpisywanie ze spacjami jest wygodniejsze, ale do backendu ma trafić
                // numer ciągiem — normalizujemy po wyjściu z pola.
                onBlur={(e) => {
                    const normalized = normalizeIban(value);
                    if (normalized !== value) setValue(normalized);
                    onBlur(e as React.FocusEvent<HTMLInputElement>);
                }}
                placeholder={placeholder}
                error={error}
                helperText={helperText}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Tooltip title={hint} enterTouchDelay={0} leaveTouchDelay={5000}>
                                {/* tabIndex=-1, żeby ikona nie łapała focusu przy przechodzeniu tabem */}
                                <IconButton aria-label={hint} edge="end" size="small" tabIndex={-1}>
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );
};

export default IbanField;
